import { state, show, setPhase, log, toast } from './ui.js';
import { api } from './api.js';
import { CELL_SIZES, nextCellSize, nextSizeForSelection } from './locker-cells.js';
import { EPICENTR_PARCEL_AGENT } from './locker-ep-first.js';

// Розміри комірок живуть у листковому locker-cells.js — його імпортують ОБИДВІ
// реалізації поштоматного флоу. Тут лише перепублікація, щоб екран app_min1
// далі брав усе з flows.js.
export { CELL_SIZES, nextCellSize };
// Значення агента — одне на обидва екрани, у листковому locker-ep-first.js
// (там же правило «спершу посилки Епіцентру» на поштоматах мережі Епіцентру).
export const EPITSENTR_AGENT = EPICENTR_PARCEL_AGENT;

// Точний збіг, а не пошук підрядка. 1С підтвердила (18.09.2026), що в цьому
// методі поле завжди зветься `agent` і значення завжди рівно таке. Підрядок
// ловив зайве: у сусідньому екрані трапляється клієнт «Шкуратовський Артем
// Епіцентр», і за старим правилом його посилка отримала б епіцентрівський
// сценарій — пріоритет у списку і нижчу стелю розміру комірки.
//
// Страховка: якщо прийде значення зі словом «епіцентр», але НЕ рівне
// еталонному, пишемо попередження в лог. Логіка спрацює як для звичайної
// посилки (нічого не ламається), але ми побачимо розбіжність того ж дня, а не
// через тиждень від курʼєрів. Без цього точний збіг перетворив би будь-яку
// зміну на боці 1С на мовчазну втрату сценарію.
export function isEpAgent(agent) {
  if (!agent) return false;
  try {
    const s = String(agent).trim().toLowerCase();
    if (s === EPITSENTR_AGENT.toLowerCase()) return true;
    if (s.includes('епіцентр')) {
      try { log('agent:epicentr_mismatch', { agent: String(agent).slice(0, 128) }); } catch (_) {}
    }
    return false;
  } catch { return false; }
}

// Спостережні ознаки дії (агент посилки, ідентифікатор екрана) живуть у
// листковому locker-marks.js — його імпортують ОБИДВІ реалізації поштоматного
// флоу, щоб не розійтись копіями. Сюди їх класти не можна: flows.js тягне
// ui.js і api.js, а courier-екран має власні core-модулі.

export function sanitizeCode(code) {
  return (code || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

export function buildDeliveryIndex() {
  state.deliveryIndex = {};
  state.delivery.forEach((p) => {
    const vars = [p.parcelID, p.parcelNumber, p.barcode, p.parcel_barcode].filter(Boolean);
    vars.forEach((v) => (state.deliveryIndex[sanitizeCode(v)] = p.parcelID));
  });
}

export function buildPickupIndex() {
  state.pickupIndex = {};
  state.pickup.forEach((p) => {
    const vars = [p.parcelID, p.parcelNumber, p.barcode, p.parcel_barcode].filter(Boolean);
    vars.forEach((v) => (state.pickupIndex[sanitizeCode(v)] = p));
  });
}

export async function resolveLockerUrl(url, _tgUserIdIgnored) {
  const params = new URLSearchParams(location.search);
  const meestId = params.get('courier_meest_id');
  const tgUserId = params.get('tg_user_id');
  log('resolveLockerUrl:start', { hasMeestId: !!meestId, hasTgUserId: !!tgUserId });
  // Бекенд приймає і tg_user_id, тож здаємось лише коли немає ЖОДНОГО
  // ідентифікатора. Гарячий шлях скану — app.js resolveLocker(); ця
  // функція наразі не викликається звідти, тримаємо правило однаковим.
  if (!meestId && !tgUserId) {
    log('resolveLockerUrl:missing_identity');
    toast('Відсутній ідентифікатор кур’єра (Meest)');
    return false;
  }
  // api() автододає courier_meest_id, тому передаємо лише url
  const ctx = await api(`/locker/by-qr?url=${encodeURIComponent(url)}`, { noLoader: true });
  if (!ctx.found) {
    log('resolveLockerUrl:not_found', { errorCode: ctx.errorCode });
    const message = ctx.errorCode === 'branch_not_found_openapi'
      ? (ctx.errorMessage || 'Поштомат не знайдено у довіднику Meest')
      : 'Поштомат не знайдено';
    toast(message);
    return false;
  }
  // apply partial, cells info fetched separately by UI layer
  log('resolveLockerUrl:ok', { branchID: ctx.branch?.branchID, branchRef: ctx.branch?.branchIDRef });
  return ctx;
}

/**
 * Посилки поштомата в state. Повертає відповідь /locker/parcels (або null при
 * помилці): app.js бере з неї опрацьовані за візит для «спершу Епіцентр».
 */
export async function loadParcels(_tgUserIdIgnored, autoPhase, opts = {}) {
  if (!state.branchID) return null;
  state.loading = true;
  try {
    const params = new URLSearchParams(location.search);
    const meestId = params.get('courier_meest_id');
    log('loadParcels:start', { branchID: state.branchID, hasMeestId: !!meestId });
    // api() автододає courier_meest_id, тому достатньо передати branchID.
    // branchIDRef — щоб бекенд знайшов, що курʼєр уже вклав / по чому подав
    // «Проблему» на цій точці сьогодні (скарги й резервації — за ref).
    const refParam = state.branchRef ? `&branchIDRef=${encodeURIComponent(state.branchRef)}` : '';
    const data = await api(`/locker/parcels?branchID=${encodeURIComponent(state.branchID)}${refParam}`, opts);
    log('loadParcels:response_keys', Object.keys(data || {}));
    // Save postamatInfo for cells chip if present
    if (data && data.postamatInfo) {
      state.postamatInfo = data.postamatInfo;
    }
    const pickupRaw = (data?.pickup || []);
    // Базова кількість вже існуючих проблем (active) з бекенду
    if (typeof data?.problemCount === 'number') {
      state.problemCountBase = data.problemCount;
    } else {
      state.problemCountBase = 0;
    }
    const deliveryRawIn = (data?.delivery || []);
    log('loadParcels:counts_before_mapping', { pickup: pickupRaw.length, delivery: deliveryRawIn.length });
    state.pickup = pickupRaw.map(x => ({ ...x }));
    // Prioritize specific agent at the top of delivery list
    const deliveryRaw = deliveryRawIn.map(x => ({ ...x }));
    deliveryRaw.sort((a, b) => {
      const aKey = (isEpAgent(a.agent)) ? 0 : 1;
      const bKey = (isEpAgent(b.agent)) ? 0 : 1;
      if (aKey !== bKey) return aKey - bKey;
      // Then stable by parcelNumber or barcode string
      const an = String(a.parcelNumber || a.barcode || '');
      const bn = String(b.parcelNumber || b.barcode || '');
      return an.localeCompare(bn);
    });
    state.delivery = deliveryRaw;
    state.deliveryTotal = state.delivery.length;
    log('loadParcels:counts_after_sort', { pickup: state.pickup.length, delivery: state.delivery.length });
    buildDeliveryIndex();
    buildPickupIndex();
    // Diagnostics for empty results
    if (state.pickup.length === 0 && state.delivery.length === 0) {
      // We cannot tell exact backend reason, but common causes are: courier not linked / no parcels upstream
      log('loadParcels:empty_result');
      // Banner will be shown by UI layer inserting a container in HTML; leave actual display to app.js if present
      try { const { showDiagBanner } = await import('./ui.js');
        showDiagBanner('Немає посилок для цього поштомату. Можливі причини: кур’єр не знайдений або немає активних відправлень.');
      } catch (_) {}
    } else {
      try { const { hideDiagBanner } = await import('./ui.js'); hideDiagBanner(); } catch (_) {}
    }
    if (autoPhase) {
      if (state.pickup.length) {
        setPhase('Забір посилок');
        show('screen_pickup');
      } else {
        setPhase('Вкладення посилок');
        show('screen_delivery');
      }
    }
    return data || null;
  } catch (e) {
    log('loadParcels:error', String(e?.message || e));
    toast('Помилка завантаження посилок');
    return null;
  } finally {
    state.loading = false;
  }
}

export function guessDeliveryParcel(code) {
  return state.deliveryIndex[sanitizeCode(code)] || null;
}

// Текст для випадку, коли відсканований код не належить цьому поштомату.
// Раніше застосунок мовчав, і кур'єр не міг відрізнити «сканер не спрацював»
// від «цієї посилки тут немає» — і повідомляв про несправність поштомату.
export function scanMissMessage(code) {
  const scanned = sanitizeCode(code).slice(0, 24);
  return scanned
    ? `Посилки ${scanned} немає у списку цього поштомату`
    : 'Не вдалося прочитати код посилки';
}

// mapCellSizeForApi видалено у 1.9.1. Він мовчки перетворював XL на L, бо
// вважалось, що в API Meest розміру XL немає. Власник підтвердив 18.09.2026,
// що метод відкриття приймає і XS, і XL, — тож розмір їде в 1С такий, який
// обрав курʼєр, без підміни.

/**
 * Наступний розмір для спроби; `null` — більшого немає.
 *
 * `freeCells` — дані з `/webapp/locker/free-cells` (або null). Коли вони є,
 * епіцентрівська стеля на L не діє і розміри з нулем пропускаються; коли їх
 * немає — поведінка рівно та, що була до 1.9.1.
 */
export function nextSizeForAgent(agent, sz, freeCells = null) {
  return nextSizeForSelection({ size: sz, isEpicentr: isEpAgent(agent), freeCells });
}
